import React from 'react';
import { CheckCircleIcon, AlertTriangleIcon, XCircleIcon, TrendingUpIcon, TrendingDownIcon, ActivityIcon } from 'lucide-react';
export function IntegrationsHealthTab() {
  const healthMetrics = {
    overall: 92,
    uptime: 99.8,
    errorRate: 0.2,
    avgLatency: 145
  };
  const connectionHealth = [{
    name: 'Salesforce Production',
    status: 'healthy',
    uptime: 99.9,
    lastError: null,
    apiCalls24h: 45230,
    avgLatency: 120,
    trend: 'up'
  }, {
    name: 'HubSpot',
    status: 'healthy',
    uptime: 99.7,
    lastError: null,
    apiCalls24h: 32100,
    avgLatency: 180,
    trend: 'stable'
  }, {
    name: 'Marketo',
    status: 'warning',
    uptime: 98.5,
    lastError: '2 hours ago',
    apiCalls24h: 12450,
    avgLatency: 340,
    trend: 'down'
  }, {
    name: 'Google Ads',
    status: 'healthy',
    uptime: 99.9,
    lastError: null,
    apiCalls24h: 8920,
    avgLatency: 95,
    trend: 'up'
  }, {
    name: 'Salesforce Sandbox',
    status: 'paused',
    uptime: 100,
    lastError: null,
    apiCalls24h: 0,
    avgLatency: 0,
    trend: 'stable'
  }];
  const recentIncidents = [{
    id: 1,
    connection: 'Marketo',
    type: 'Rate Limit',
    severity: 'warning',
    timestamp: '2024-01-20 14:32',
    resolved: false,
    description: 'API rate limit exceeded during bulk sync'
  }, {
    id: 2,
    connection: 'HubSpot',
    type: 'Authentication',
    severity: 'error',
    timestamp: '2024-01-19 09:15',
    resolved: true,
    description: 'OAuth token refresh failed'
  }, {
    id: 3,
    connection: 'Salesforce Production',
    type: 'Timeout',
    severity: 'warning',
    timestamp: '2024-01-18 16:45',
    resolved: true,
    description: 'Query timeout on large data export'
  }];
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'healthy':
        return <CheckCircleIcon className="w-5 h-5 text-green-600" data-id="element-1742" />;
      case 'warning':
        return <AlertTriangleIcon className="w-5 h-5 text-yellow-600" data-id="element-1743" />;
      case 'error':
        return <XCircleIcon className="w-5 h-5 text-red-600" data-id="element-1744" />;
      case 'paused':
        return <CheckCircleIcon className="w-5 h-5 text-gray-400" data-id="element-1745" />;
      default:
        return <CheckCircleIcon className="w-5 h-5 text-gray-400" data-id="element-1746" />;
    }
  };
  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up':
        return <TrendingUpIcon className="w-4 h-4 text-green-600" data-id="element-1747" />;
      case 'down':
        return <TrendingDownIcon className="w-4 h-4 text-red-600" data-id="element-1748" />;
      default:
        return <ActivityIcon className="w-4 h-4 text-gray-400" data-id="element-1749" />;
    }
  };
  return <div className="space-y-6" data-id="element-1750">
      {/* Overall Health Score */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-lg p-6 text-white" data-id="element-1751">
        <div className="flex items-center justify-between" data-id="element-1752">
          <div data-id="element-1753">
            <h2 className="text-2xl font-bold mb-2" data-id="element-1754">Overall Health Score</h2>
            <p className="text-green-100" data-id="element-1755">All systems operational</p>
          </div>
          <div className="text-6xl font-bold" data-id="element-1756">{healthMetrics.overall}%</div>
        </div>
      </div>
      {/* Key Metrics */}
      <div className="grid grid-cols-4 gap-4" data-id="element-1757">
        <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1758">
          <p className="text-sm text-gray-600 mb-1" data-id="element-1759">Uptime (30d)</p>
          <p className="text-3xl font-bold text-gray-900" data-id="element-1760">
            {healthMetrics.uptime}%
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1761">
          <p className="text-sm text-gray-600 mb-1" data-id="element-1762">Error Rate</p>
          <p className="text-3xl font-bold text-gray-900" data-id="element-1763">
            {healthMetrics.errorRate}%
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1764">
          <p className="text-sm text-gray-600 mb-1" data-id="element-1765">Avg Latency</p>
          <p className="text-3xl font-bold text-gray-900" data-id="element-1766">
            {healthMetrics.avgLatency}ms
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1767">
          <p className="text-sm text-gray-600 mb-1" data-id="element-1768">Active Connections</p>
          <p className="text-3xl font-bold text-gray-900" data-id="element-1769">4</p>
        </div>
      </div>
      {/* Connection Health Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-id="element-1770">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-1771">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-1772">
            Connection Health
          </h3>
        </div>
        <table className="w-full" data-id="element-1773">
          <thead className="bg-gray-50 border-b border-gray-200" data-id="element-1774">
            <tr data-id="element-1775">
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-1776">
                Connection
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-1777">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-1778">
                Uptime (30d)
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-1779">
                Last Error
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-1780">
                API Calls (24h)
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-1781">
                Avg Latency
              </th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider" data-id="element-1782">
                Trend
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200" data-id="element-1783">
            {connectionHealth.map((connection, index) => <tr key={index} className="hover:bg-gray-50" data-id="element-1784">
                <td className="px-6 py-4" data-id="element-1785">
                  <div className="flex items-center gap-2" data-id="element-1786">
                    {getStatusIcon(connection.status)}
                    <span className="text-sm font-medium text-gray-900" data-id="element-1787">
                      {connection.name}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4" data-id="element-1788">
                  <span className={`px-2 py-1 rounded text-xs font-medium capitalize ${connection.status === 'healthy' ? 'bg-green-100 text-green-700' : connection.status === 'warning' ? 'bg-yellow-100 text-yellow-700' : connection.status === 'error' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`} data-id="element-1789">
                    {connection.status}
                  </span>
                </td>
                <td className="px-6 py-4" data-id="element-1790">
                  <span className="text-sm text-gray-900" data-id="element-1791">
                    {connection.uptime}%
                  </span>
                </td>
                <td className="px-6 py-4" data-id="element-1792">
                  <span className="text-sm text-gray-900" data-id="element-1793">
                    {connection.lastError || 'None'}
                  </span>
                </td>
                <td className="px-6 py-4" data-id="element-1794">
                  <span className="text-sm text-gray-900" data-id="element-1795">
                    {connection.apiCalls24h.toLocaleString()}
                  </span>
                </td>
                <td className="px-6 py-4" data-id="element-1796">
                  <span className="text-sm text-gray-900" data-id="element-1797">
                    {connection.avgLatency}ms
                  </span>
                </td>
                <td className="px-6 py-4" data-id="element-1798">{getTrendIcon(connection.trend)}</td>
              </tr>)}
          </tbody>
        </table>
      </div>
      {/* Recent Incidents */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-1799">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-1800">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-1801">
            Recent Incidents
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-1802">
          {recentIncidents.map(incident => <div key={incident.id} className="p-6" data-id="element-1803">
              <div className="flex items-start justify-between mb-2" data-id="element-1804">
                <div className="flex items-center gap-3" data-id="element-1805">
                  {incident.severity === 'error' ? <XCircleIcon className="w-5 h-5 text-red-600" data-id="element-1806" /> : <AlertTriangleIcon className="w-5 h-5 text-yellow-600" data-id="element-1807" />}
                  <div data-id="element-1808">
                    <p className="text-sm font-semibold text-gray-900" data-id="element-1809">
                      {incident.connection} - {incident.type}
                    </p>
                    <p className="text-xs text-gray-600" data-id="element-1810">
                      {incident.timestamp}
                    </p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded text-xs font-medium ${incident.resolved ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`} data-id="element-1811">
                  {incident.resolved ? 'Resolved' : 'Active'}
                </span>
              </div>
              <p className="text-sm text-gray-700 ml-8" data-id="element-1812">
                {incident.description}
              </p>
            </div>)}
        </div>
      </div>
    </div>;
}